import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stack,
} from '@mui/material';
import {
  Check as CheckIcon,
  PlaylistAdd as PlaylistAddIcon,
  InfoOutlined,
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { createLabourItemDefinition } from '../../../../../labour/api/labourItemsApi';

const MAX_VISIBLE_ITEMS = 5;

interface MissingLabourItemsCreatorProps {
  missingItems: string[];
  onAllResolved: () => void;
}

const MissingLabourItemsCreator: React.FC<MissingLabourItemsCreatorProps> = ({
  missingItems,
  onAllResolved,
}) => {
  const [createdItems, setCreatedItems] = useState<Set<string>>(new Set());
  const [creationErrors, setCreationErrors] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  const createMutation = useMutation({
    mutationFn: (itemName: string) =>
      createLabourItemDefinition({ name: itemName }),
  });

  const remainingItems = missingItems.filter(item => !createdItems.has(item));
  const allItemsCreated =
    missingItems.length > 0 && remainingItems.length === 0;

  const handleCreateAll = async () => {
    setCreating(true);
    setCreationErrors([]);
    const newCreated = new Set(createdItems);
    const errors: string[] = [];

    for (const item of remainingItems) {
      try {
        await createMutation.mutateAsync(item);
        newCreated.add(item);
        setCreatedItems(new Set(newCreated));
      } catch {
        errors.push(`Failed to create "${item}"`);
      }
    }

    setCreationErrors(errors);
    setCreating(false);

    if (newCreated.size === missingItems.length) {
      onAllResolved();
    }
  };

  if (!missingItems.length) {
    return null;
  }

  const hasRemainingItems = remainingItems.length > 0;

  return (
    <Card
      elevation={2}
      sx={{
        border: '1px solid',
        borderColor: allItemsCreated ? 'success.light' : 'info.light',
        bgcolor: allItemsCreated ? 'success.50' : 'info.50',
      }}
      data-testid="missing-labour-items-creator"
    >
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={2}>
          {/* Header Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <InfoOutlined
              color={allItemsCreated ? 'success' : 'info'}
              fontSize="small"
            />
            <Typography
              variant="subtitle1"
              color={allItemsCreated ? 'success.dark' : 'info.dark'}
              fontWeight={600}
              data-testid="missing-labour-items-title"
            >
              {allItemsCreated
                ? 'All labour items created'
                : 'New labour items needed'}
            </Typography>
            {!allItemsCreated && (
              <Chip
                label={`${remainingItems.length} remaining`}
                size="small"
                color="info"
                variant="outlined"
              />
            )}
          </Box>

          {/* Explanatory text */}
          {hasRemainingItems && (
            <Typography variant="body2" color="text.secondary">
              Your import references items that don&apos;t exist yet. Create
              them below so the import can proceed.
            </Typography>
          )}

          {/* Error Messages */}
          {creationErrors.length > 0 && (
            <>
              <Divider />
              <Alert
                severity="error"
                data-testid="missing-labour-items-creation-errors"
              >
                <Typography variant="subtitle2" gutterBottom>
                  Failed to create some items:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {creationErrors.map((error, index) => (
                    <Typography component="li" key={index} variant="body2">
                      {error}
                    </Typography>
                  ))}
                </Box>
              </Alert>
            </>
          )}

          {/* Missing Items List */}
          {hasRemainingItems && (
            <List
              dense
              disablePadding
              sx={{
                maxHeight: MAX_VISIBLE_ITEMS * 48,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
              }}
              data-testid="missing-labour-items-chips"
            >
              {remainingItems.map(item => (
                <ListItem
                  key={item}
                  divider
                  sx={{
                    '&:last-child': { borderBottom: 0 },
                  }}
                >
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      variant: 'body2',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {/* Create All button — below the list */}
          {hasRemainingItems && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={
                  creating ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PlaylistAddIcon />
                  )
                }
                onClick={handleCreateAll}
                disabled={creating}
                color="primary"
                sx={{ minWidth: 120 }}
                data-testid="create-all-missing-labour-items-button"
              >
                {creating ? 'Creating...' : 'Create All'}
              </Button>
            </Box>
          )}

          {/* Success Message */}
          {allItemsCreated && (
            <Alert
              severity="success"
              icon={<CheckIcon />}
              data-testid="missing-labour-items-success"
            >
              All items created. You can now proceed.
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default MissingLabourItemsCreator;
